import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const HASH_RESOLVE_TIMEOUT_MS = 1500;
const HASH_RESOLVE_INTERVAL_MS = 50;

/**
 * Scroll management on route changes.
 * - If a hash is present, waits for the matching element to mount and stabilize.
 * - Otherwise scrolls to top.
 * - Skips on POP (back/forward) so browser can restore position.
 */
export function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === 'POP') return;

    if (hash) {
      const id = decodeURIComponent(hash.replace(/^#/, ''));
      const startedAt = Date.now();
      let lastTop: number | null = null;
      let stableCount = 0;
      let timeoutId: number | undefined;

      const tryScroll = () => {
        const el = document.getElementById(id);

        if (!el) {
          if (Date.now() - startedAt < HASH_RESOLVE_TIMEOUT_MS) {
            timeoutId = window.setTimeout(tryScroll, HASH_RESOLVE_INTERVAL_MS);
          } else {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
          }
          return;
        }

        const top = Math.round(el.getBoundingClientRect().top + window.scrollY);

        if (top === lastTop) {
          stableCount += 1;
        } else {
          stableCount = 0;
          lastTop = top;
        }

        if (stableCount >= 2 || Date.now() - startedAt >= HASH_RESOLVE_TIMEOUT_MS) {
          el.scrollIntoView({ behavior: 'instant' as ScrollBehavior, block: 'start' });
        } else {
          timeoutId = window.setTimeout(tryScroll, HASH_RESOLVE_INTERVAL_MS);
        }
      };

      tryScroll();
      return () => {
        if (timeoutId) window.clearTimeout(timeoutId);
      };
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash, navigationType]);

  return null;
}
